import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { useLocation, useMatches } from "react-router";
import { gql, useQuery } from "@apollo/client";
import { FeatureFlagContext } from "./FeatureFlagContext";
import { useWorkbenchSession } from "shared/contextProviders/workbenchSession";

/**
 * `FeatureFlagProvider` sits under `/app` (Root). `useParams()` there does not
 * include `organizationId` from child segments. The `organization/:organizationId`
 * route has no `Component` (only children), so `useMatches()` may not expose
 * `organizationId` on every RR version / lazy boundary. Parse the canonical
 * org URL shape and fall back to scanning matches.
 */
function useFeatureFlagTargetIds(): {
  organizationId?: string;
  storefrontId?: string;
} {
  const { pathname } = useLocation();
  const matches = useMatches();

  return useMemo(() => {
    const storefrontId = pathname.match(/\/storefront\/([^/]+)/)?.[1];
    const fromPath = pathname.match(/^\/app\/organization\/([^/]+)/)?.[1];
    if (fromPath) return { organizationId: fromPath, storefrontId };

    for (const match of matches) {
      const id = match.params?.organizationId;
      if (typeof id === "string" && id.length > 0) {
        return { organizationId: id, storefrontId };
      }
    }
    return { storefrontId };
  }, [pathname, matches]);
}

const FEATURE_FLAGS_BY_ORG = gql`
  query FeatureFlagsByOrganization($organizationId: MongoID!) {
    featureFlagsByOrganization(organizationId: $organizationId) {
      _id
      key
      group
      enabled
      graduated
      hasExplicitGate
    }
  }
`;

const FEATURE_FLAGS_BY_STOREFRONT = gql`
  query FeatureFlagsByStorefront(
    $storefrontId: MongoID!
    $organizationId: MongoID
  ) {
    featureFlagsByStorefront(
      storefrontId: $storefrontId
      organizationId: $organizationId
    ) {
      _id
      key
      group
      enabled
      graduated
      hasExplicitGate
    }
  }
`;

type ResolvedFlag = {
  _id: string;
  key: string;
  group: string;
  enabled: boolean;
  graduated: boolean;
};

type Props = {
  children: ReactNode;
};

export const FeatureFlagProvider = ({ children }: Props) => {
  const { organizationId, storefrontId } = useFeatureFlagTargetIds();
  const { session: workbenchSession, record: recordWorkbenchEvent } =
    useWorkbenchSession();
  const workbenchFlagKey = workbenchSession?.flagKey;

  const { data: organizationData, loading: organizationLoading } = useQuery<{
    featureFlagsByOrganization: ResolvedFlag[];
  }>(FEATURE_FLAGS_BY_ORG, {
    variables: { organizationId },
    skip: !organizationId,
    fetchPolicy: "cache-and-network",
  });
  const { data: storefrontData, loading: storefrontLoading } = useQuery<{
    featureFlagsByStorefront: ResolvedFlag[];
  }>(FEATURE_FLAGS_BY_STOREFRONT, {
    variables: { storefrontId, organizationId },
    skip: !storefrontId,
    fetchPolicy: "cache-and-network",
  });

  const flags = useMemo(() => {
    const map = new Map<string, { enabled: boolean; group: string }>();
    const resolvedFlags = storefrontId
      ? (storefrontData?.featureFlagsByStorefront ??
        organizationData?.featureFlagsByOrganization)
      : organizationData?.featureFlagsByOrganization;
    if (resolvedFlags) {
      for (const flag of resolvedFlags) {
        map.set(flag.key, { enabled: flag.enabled, group: flag.group });
      }
    }
    return map;
  }, [organizationData, storefrontData, storefrontId]);
  const loading = storefrontId
    ? storefrontLoading && organizationLoading
    : organizationLoading;

  const realWorkbenchFlagEnabled = workbenchFlagKey
    ? flags.get(workbenchFlagKey)?.enabled
    : undefined;
  useEffect(() => {
    if (!workbenchFlagKey || realWorkbenchFlagEnabled === undefined) return;
    recordWorkbenchEvent({
      type: "flag",
      flagKey: workbenchFlagKey,
      enabled: realWorkbenchFlagEnabled,
      organizationId,
      message: "backend gate for this org (the Workbench session forces the UI on)",
    });
  }, [workbenchFlagKey, realWorkbenchFlagEnabled, organizationId, recordWorkbenchEvent]);

  const isEnabled = useCallback(
    (flagKey: string): boolean => {
      if (workbenchFlagKey === flagKey) return true;
      return flags.get(flagKey)?.enabled ?? false;
    },
    [flags, workbenchFlagKey],
  );

  const isGroupEnabled = useCallback(
    (group: string): boolean => {
      const groupFlags = Array.from(flags.values()).filter(
        f => f.group === group,
      );
      if (groupFlags.length === 0) return false;
      return groupFlags.every(f => f.enabled);
    },
    [flags],
  );

  const value = useMemo(
    () => ({ isEnabled, isGroupEnabled, flags, loading }),
    [isEnabled, isGroupEnabled, flags, loading],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
